import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Globe, ChevronLeft, ChevronRight, LayoutGrid, X, Maximize2, Minimize2,
  Key, LayoutDashboard, Wallet, Wrench, Megaphone, FileText, CalendarDays, ScrollText,
  Shield, Receipt, HelpCircle, Check, Pause,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// Public flow reference ("Flujos del Portal") — a slideshow tracing the typical
// end-to-end flow for each role, one task per slide, every step a real link into
// the app. Reachable from the login screen and from the in-app Help Center
// without needing a specific account. Bilingual via the app's i18n language.

type Lang = "es" | "en";
type Pair = readonly [string, string];
const P = (es: string, en: string): Pair => [es, en] as const;

function useTr() {
  const { i18n } = useTranslation();
  const lang: Lang = i18n.language.startsWith("en") ? "en" : "es";
  const t = (p: Pair) => (lang === "en" ? p[1] : p[0]);
  return { lang, t, i18n };
}

/* ---------- role colors (same coding as GuiaVisualPage / AppLayout) ---------- */
const GOLD6 = "#a8853f", GOLD_TINT = "#d4ae54", GOLD_SOFT = "#f4e9cf";
const OCEAN = "#2f7896", OCEAN_TINT = "#76b6cd", OCEAN_SOFT = "#dcecf1";
const NAVY = "#1e4d6b", NAVY_TINT = "#90bbd7", NAVY_SOFT = "#dbe6ec";
const BRONZE = "#866932", BRONZE_TINT = "#dfc479", BRONZE_SOFT = "#ece2cc";

const ICONS: Record<string, LucideIcon> = {
  key: Key, grid: LayoutDashboard, ask: HelpCircle, wallet: Wallet, wrench: Wrench,
  megaphone: Megaphone, doc: FileText, calendar: CalendarDays, scroll: ScrollText,
  shield: Shield, receipt: Receipt,
};

/* ---------- step model ---------- */
type Step =
  | { type: "node"; iconName: string; title: Pair; desc: Pair; route?: string; info?: boolean; sub?: { k: Pair; v: Pair }; arrowLabel?: Pair }
  | { type: "outcome"; text: Pair; warn?: boolean; arrowLabel?: Pair }
  | { type: "fork"; question: Pair; leftLabel: Pair; rightLabel: Pair; leftOutcome: Pair; rightOutcome: Pair };

interface Lane { label: Pair; chain: Step[] }
interface Journey {
  id: string; color: string; tint: string; soft: string; num: string;
  eyebrow: Pair; title: Pair; blurb: Pair;
  entryIcon: string; entryTitle: Pair; entryRoute: string;
  lanes: Lane[]; footnote?: Pair;
}

const JOURNEYS: Journey[] = [
  {
    id: "inicio", color: GOLD6, tint: GOLD_TINT, soft: GOLD_SOFT, num: "0",
    eyebrow: P("Para todos", "For everyone"), title: P("Primeros pasos", "Getting started"),
    blurb: P("Lo que ocurre en cada sesión, sin importar el rol — el punto de entrada a los otros tres flujos.", "What happens in every session, whatever the role — the entry point to the other three flows."),
    entryIcon: "key", entryTitle: P("Iniciar sesión", "Sign in"), entryRoute: "/login",
    lanes: [
      { label: P("Panel principal", "Main dashboard"), chain: [
        { type: "node", iconName: "key", title: P("Iniciar sesión", "Sign in"), desc: P("Correo y contraseña asignados por la administración.", "Email and password assigned by the administration."), route: "/login" },
        { type: "node", iconName: "grid", title: P("El Panel", "The Dashboard"), desc: P("Tarjetas con lo pendiente.", "Cards showing what's pending."), route: "/dashboard" },
        { type: "outcome", text: P("Va directo al módulo correspondiente", "Jumps straight to the relevant module"), arrowLabel: P("clic en tarjeta", "click a card") },
      ] },
      { label: P("El Asistente", "The Assistant"), chain: [
        { type: "node", iconName: "key", title: P("Iniciar sesión", "Sign in"), desc: P("Disponible desde cualquier pantalla tras entrar.", "Available from any screen once signed in."), route: "/login" },
        { type: "node", iconName: "ask", title: P("Preguntar", "Ask"), desc: P("En cada pantalla — no es una página aparte.", "On every screen — not a separate page."), info: true },
        { type: "outcome", text: P("Duda resuelta, sin releer el Reglamento", "Question answered, no need to re-read the Reglamento"), arrowLabel: P("con regla y cita", "with rule and citation") },
      ] },
    ],
  },
  {
    id: "residente", color: OCEAN, tint: OCEAN_TINT, soft: OCEAN_SOFT, num: "1",
    eyebrow: P("Residente / Titular", "Resident / Owner"), title: P("El día a día del titular", "The owner's day-to-day"),
    blurb: P("Pagar, solicitar, reportar y mantenerse informado — todo desde la cuenta del dueño de una unidad.", "Pay, request, report, and stay informed — all from the owner's own account."),
    entryIcon: "grid", entryTitle: P("El Panel", "The Dashboard"), entryRoute: "/dashboard",
    footnote: P("También disponibles, sin flujo propio: Finanzas y Documentos.", "Also available, with no flow of their own: Finances and Documents."),
    lanes: [
      { label: P("Pagar una cuota", "Pay a fee"), chain: [
        { type: "node", iconName: "receipt", title: P("Mis Cuotas", "My Fees"), desc: P("Balance por mes; recargo del 10% tras 30 días de gracia.", "Balance by month; 10% late charge after a 30-day grace period."), route: "/resident/fees" },
        { type: "outcome", text: P("Recibo en PDF", "Receipt in PDF"), arrowLabel: P("descargar recibo", "download receipt") },
      ] },
      { label: P("Solicitar una obra", "Request work"), chain: [
        { type: "node", iconName: "wrench", title: P("Solicitudes de Titulares", "Works Permits"), desc: P("El formulario muestra la fianza y el horario del Manual.", "The form shows the bond and hours required by the Manual."), route: "/resident/works-permit" },
        { type: "fork", question: P("La Junta revisa", "The board reviews"), leftLabel: P("Aprobado", "Approved"), rightLabel: P("Con condiciones", "With conditions"), leftOutcome: P("Resolución PDF generada", "Resolution PDF generated"), rightOutcome: P("Titular ajusta y reenvía", "Owner adjusts and resubmits") },
      ] },
      { label: P("Reportar una avería", "Report a repair"), chain: [
        { type: "node", iconName: "wrench", title: P("Mantenimiento", "Maintenance"), desc: P("Describe el problema y la ubicación.", "Describe the problem and its location."), route: "/resident/maintenance" },
        { type: "outcome", text: P("Resuelto — sin tener que llamar", "Resolved — without having to call"), arrowLabel: P("abierto → en progreso", "open → in progress") },
      ] },
    ],
  },
  {
    id: "junta", color: NAVY, tint: NAVY_TINT, soft: NAVY_SOFT, num: "2",
    eyebrow: P("Junta & Administración", "Board & Administration"), title: P("Gobernar y administrar", "Govern and administer"),
    blurb: P("Las decisiones de gobierno, respaldadas por la regla exacta en el momento en que se toman.", "Governance decisions, backed by the exact rule at the moment they're made."),
    entryIcon: "grid", entryTitle: P("El Panel", "The Dashboard"), entryRoute: "/dashboard",
    footnote: P("El registro de Unidades es consulta permanente, no parte de un flujo.", "The Units registry is a standing reference, not part of a flow."),
    lanes: [
      { label: P("Comunicar", "Communicate"), chain: [
        { type: "node", iconName: "megaphone", title: P("Comunicados", "Announcements"), desc: P("En avisos «Reglamentarios» aparecen las normas a citar.", "«Regulatory» notices show the rules to cite."), route: "/resident/announcements" },
        { type: "outcome", text: P("Correo a todos los titulares", "Email to every owner"), arrowLabel: P("publicar y enviar", "publish and send") },
      ] },
      { label: P("Convocar reunión", "Call a meeting"), chain: [
        { type: "node", iconName: "calendar", title: P("Reuniones", "Meetings"), desc: P("El sistema recuerda plazos de convocatoria y quórum.", "The system reminds you of notice deadlines and quorum."), route: "/meetings", sub: { k: P("Detalle:", "Detail:"), v: P("borrador del acta →", "minutes draft →") } },
        { type: "outcome", text: P("Acta en PDF enviada a todos", "Minutes PDF sent to everyone"), arrowLabel: P("aprobar y distribuir", "approve and distribute") },
      ] },
      { label: P("Revisar un permiso", "Review a permit"), chain: [
        { type: "node", iconName: "wrench", title: P("Solicitudes de Titulares", "Works Permits"), desc: P("Revisa que esté completa y la envía a Resoluciones.", "Checks it is complete and sends it to Resolutions."), route: "/resident/works-permit" },
        { type: "node", iconName: "scroll", title: P("Resoluciones", "Resolutions"), desc: P("Con la solicitud y su documentación a la vista; añade condiciones.", "With the request and its documents at hand; adds conditions."), route: "/resolutions" },
        { type: "fork", question: P("Decisión de la Junta", "The board's decision"), leftLabel: P("Aprobar", "Approve"), rightLabel: P("Rechazar", "Reject"), leftOutcome: P("Resolución numerada; luego se sube la firmada", "Numbered resolution; the signed copy is uploaded later"), rightOutcome: P("Solicitud denegada; notifica al titular", "Request denied; notifies the owner") },
      ] },
      { label: P("Registrar un incidente", "Log an incident"), chain: [
        { type: "node", iconName: "shield", title: P("Incidentes", "Incidents"), desc: P("«Violación de reglamento» muestra la escala oficial.", "«Rule violation» shows the official fine schedule."), route: "/admin/incidents" },
        { type: "outcome", text: P("Multa registrada en el historial", "Fine logged in the history") },
      ] },
      { label: P("Crear una resolución", "Create a resolution"), chain: [
        { type: "node", iconName: "scroll", title: P("Resoluciones", "Resolutions"), desc: P("Cada miembro vota; el conteo se lleva solo.", "Each member votes; the tally is kept automatically."), route: "/resolutions" },
        { type: "fork", question: P("Al cerrar la votación", "When voting closes"), leftLabel: P("Mayoría a favor", "Majority in favor"), rightLabel: P("Mayoría en contra", "Majority against"), leftOutcome: P("Aprobada — registro y PDF", "Approved — record and PDF"), rightOutcome: P("Rechazada — queda en registro", "Rejected — stays on record") },
      ] },
    ],
  },
  {
    id: "contable", color: BRONZE, tint: BRONZE_TINT, soft: BRONZE_SOFT, num: "3",
    eyebrow: P("Contable", "Bookkeeper"), title: P("Llevar los libros, sin duplicar", "Keeping the books, without duplication"),
    blurb: P("El contable trabaja en QuickBooks; el portal es la capa de cobro y transparencia hacia los titulares.", "The bookkeeper works in QuickBooks; the portal is the collections and transparency layer for owners."),
    entryIcon: "wallet", entryTitle: P("Tablero Financiero", "Finance Dashboard"), entryRoute: "/finance/dashboard",
    lanes: [
      { label: P("Registrar un gasto", "Record an expense"), chain: [
        { type: "node", iconName: "wallet", title: P("Gastos", "Expenses"), desc: P("Mismo catálogo de cuentas que QuickBooks, con su código.", "Same chart of accounts as QuickBooks, with its code."), route: "/finance/expenses" },
        { type: "outcome", text: P("Gasto registrado", "Expense recorded"), arrowLabel: P("guardar", "save") },
      ] },
      { label: P("Generar cuotas del mes", "Generate the month's fees"), chain: [
        { type: "node", iconName: "receipt", title: P("Cuotas", "Fees"), desc: P("Elige período y fecha de vencimiento.", "Choose the period and due date."), route: "/finance/fees" },
        { type: "outcome", text: P("Cada unidad con su cuota mensual", "Each unit billed its monthly fee"), arrowLabel: P("generar cuotas", "generate fees") },
      ] },
      { label: P("Cerrar el mes", "Close the month"), chain: [
        { type: "node", iconName: "doc", title: P("Cierre Mensual", "Monthly Close"), desc: P("Sube el P&L en PDF, ingresa las cifras clave.", "Upload the P&L PDF, enter the key figures."), route: "/finance/close" },
        { type: "fork", question: P("Al terminar el cierre", "When the close is done"), leftLabel: P("Publicar", "Publish"), rightLabel: P("Guardar borrador", "Save draft"), leftOutcome: P("Visible en Finanzas del titular", "Visible on the owner's Finances page"), rightOutcome: P("Pendiente de publicar", "Pending publish") },
      ] },
    ],
  },
];

/* ---------- presentational primitives ---------- */

function StepIcon({ name, size = 20 }: { name: string; size?: number }) {
  const Cmp = ICONS[name];
  if (!Cmp) return null;
  return <Cmp size={size} className="flp-icon" />;
}

function StepNode({ step, t, color }: { step: Extract<Step, { type: "node" }>; t: (p: Pair) => string; color: string }) {
  const inner = (
    <>
      <div className="flp-node-top">
        <span style={{ color }}><StepIcon name={step.iconName} size={22} /></span>
        <span className="flp-node-title">{t(step.title)}</span>
      </div>
      <div className="flp-node-desc">{t(step.desc)}</div>
      {step.route && <div className="flp-node-route">{step.route}</div>}
      {step.sub && (
        <div className="flp-node-sub"><b>{t(step.sub.k)}</b> {t(step.sub.v)}</div>
      )}
    </>
  );
  if (step.info || !step.route) return <div className="flp-node info">{inner}</div>;
  return (
    <a className="flp-node" href={step.route} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  );
}

function Outcome({ text, warn }: { text: string; warn?: boolean }) {
  return (
    <div className={"flp-outcome" + (warn ? " warn" : "")}>
      {warn ? <Pause size={15} /> : <Check size={16} />}
      {text}
    </div>
  );
}

function ArrowH({ label }: { label?: string }) {
  return (
    <div className="flp-chain-arrow">
      <svg width="88" height="40" viewBox="0 0 88 40" aria-hidden={label ? undefined : true} role={label ? "img" : undefined} aria-label={label ? `Continúa: ${label}` : undefined}>
        <line x1="4" y1="30" x2="76" y2="30" stroke="currentColor" strokeWidth={1.8} />
        <polygon points="76,25 76,35 86,30" fill="currentColor" />
      </svg>
      {label && <span>{label}</span>}
    </div>
  );
}

function ForkArrows({ leftLabel, rightLabel }: { leftLabel: string; rightLabel: string }) {
  const w = 260, h = 40, cx = w / 2, leftX = w * 0.24, rightX = w * 0.76;
  return (
    <svg className="flp-fork-svg" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`Se bifurca en ${leftLabel} o ${rightLabel}`}>
      <line x1={cx} y1={0} x2={cx} y2={9} stroke="currentColor" strokeWidth={1.6} opacity={0.5} />
      <path d={`M ${cx} 9 L ${leftX} ${h - 9}`} stroke="currentColor" strokeWidth={1.6} fill="none" opacity={0.5} />
      <path d={`M ${cx} 9 L ${rightX} ${h - 9}`} stroke="currentColor" strokeWidth={1.6} fill="none" opacity={0.5} />
      <polygon points={`${leftX - 5},${h - 9} ${leftX + 5},${h - 9} ${leftX},${h}`} fill="currentColor" opacity={0.5} />
      <polygon points={`${rightX - 5},${h - 9} ${rightX + 5},${h - 9} ${rightX},${h}`} fill="currentColor" opacity={0.5} />
      <text x={leftX} y={h - 15} fontSize={12} fontWeight={700} textAnchor="middle" fill="var(--flp-good)">{leftLabel}</text>
      <text x={rightX} y={h - 15} fontSize={12} fontWeight={700} textAnchor="middle" fill="var(--flp-coral)">{rightLabel}</text>
    </svg>
  );
}

function ForkBox({ step, t }: { step: Extract<Step, { type: "fork" }>; t: (p: Pair) => string }) {
  return (
    <div className="flp-fork">
      <div className="flp-fork-q"><HelpCircle size={17} />{t(step.question)}</div>
      <ForkArrows leftLabel={t(step.leftLabel)} rightLabel={t(step.rightLabel)} />
      <div className="flp-branches">
        <Outcome text={t(step.leftOutcome)} />
        <Outcome text={t(step.rightOutcome)} warn />
      </div>
    </div>
  );
}

function Chain({ steps, t, color }: { steps: Step[]; t: (p: Pair) => string; color: string }) {
  return (
    <div className="flp-chain">
      {steps.map((s, i) => (
        <FragmentStep key={i} step={s} first={i === 0} t={t} color={color} />
      ))}
    </div>
  );
}
function FragmentStep({ step, first, t, color }: { step: Step; first: boolean; t: (p: Pair) => string; color: string }) {
  const arrowLabel = step.type !== "fork" && "arrowLabel" in step && step.arrowLabel ? t(step.arrowLabel) : undefined;
  return (
    <>
      {!first && <ArrowH label={arrowLabel} />}
      {step.type === "node" && <StepNode step={step} t={t} color={color} />}
      {step.type === "outcome" && <Outcome text={t(step.text)} warn={step.warn} />}
      {step.type === "fork" && <ForkBox step={step} t={t} />}
    </>
  );
}

function Crumb({ iconName, title, route, color }: { iconName: string; title: string; route: string; color: string }) {
  return (
    <Link className="flp-crumb" to={route}>
      <span style={{ color }}><StepIcon name={iconName} size={15} /></span>
      <span>{title}</span>
    </Link>
  );
}

/* ---------- slide model ---------- */

/* ---------- roles & seats: who can do what (mirrors the API guards) ---------- */
interface RoleRow { role: Pair; who: Pair; can: Pair[]; not?: Pair }
const ROLES: RoleRow[] = [
  {
    role: P("Administración", "Administration"),
    who: P("Rol ADMIN", "ADMIN role"),
    can: [
      P("Todo el portal: usuarios, unidades, personal, llaves, cámaras, registro de auditoría.", "The whole portal: users, units, staff, keys, cameras, audit log."),
      P("Asigna roles, cargos de la Junta y permisos; elimina registros; crea los usuarios titulares.", "Assigns roles, board seats and permissions; deletes records; creates owner logins."),
    ],
  },
  {
    role: P("Secretaria", "Secretary"),
    who: P("Junta · cargo SECRETARY", "Board · SECRETARY seat"),
    can: [
      P("Convoca reuniones: crea la reunión, genera el enlace de Meet, envía las invitaciones y la edita.", "Convenes meetings: creates the meeting, mints the Meet link, sends invitations and edits it."),
      P("Todo lo de la Junta (más abajo).", "Everything the Board can do (below)."),
    ],
  },
  {
    role: P("Tesorero", "Treasurer"),
    who: P("Junta · cargo TREASURER", "Board · TREASURER seat"),
    can: [
      P("Gestión financiera completa sin permiso adicional: generar cuotas, cierre mensual, informes, presupuestos.", "Full finance management with no extra grant: generate fees, monthly close, reports, budgets."),
      P("Todo lo de la Junta (más abajo).", "Everything the Board can do (below)."),
    ],
  },
  {
    role: P("Presidente, Vicepresidente y Directivos", "President, Vice president and Directors"),
    who: P("Junta · rol BOARD_MEMBER", "Board · BOARD_MEMBER role"),
    can: [
      P("Reuniones y actas: ver, comentar, aprobar y adjuntar; votar resoluciones.", "Meetings and minutes: view, comment, approve and attach; vote on resolutions."),
      P("Comunicados, documentos, incidentes, contratos y acciones legales; revisar permisos de obra y mantenimiento.", "Announcements, documents, incidents, contracts and legal actions; review works permits and maintenance."),
      P("Registrar gastos y revisar los de otros (nunca los propios); ver el tablero financiero.", "Record expenses and review others' (never their own); see the finance dashboard."),
    ],
    not: P("No convocan reuniones ni generan cuotas.", "Cannot convene meetings or generate fees."),
  },
  {
    role: P("Contable", "Bookkeeper"),
    who: P("Permiso «Gestionar finanzas»", "“Manage finances” permission"),
    can: [P("Cuotas, gastos, presupuestos, informes y cierre mensual, sin ser miembro de la Junta.", "Fees, expenses, budgets, reports and monthly close, without a board seat.")],
    not: P("No ve el resto de la gobernanza.", "Does not see the rest of governance."),
  },
  {
    role: P("Residente / Titular", "Resident / Owner"),
    who: P("Rol RESIDENT vinculado a su unidad", "RESIDENT role linked to their unit"),
    can: [
      P("Sus cuotas y pagos, sus permisos de obra y solicitudes de mantenimiento.", "Their fees and payments, their works permits and maintenance requests."),
      P("Comunicados publicados, documentos de gobierno y próximas reuniones.", "Published announcements, governing documents and upcoming meetings."),
    ],
    not: P("No ve datos de otras unidades ni de la Junta.", "Cannot see other units' or board data."),
  },
];

type Slide =
  | { kind: "cover" }
  | { kind: "divider"; journey: Journey }
  | { kind: "lane"; journey: Journey; lane: Lane }
  | { kind: "roles" }
  | { kind: "closing" };

const SLIDES: Slide[] = [
  { kind: "cover" },
  ...JOURNEYS.flatMap((j) => [
    { kind: "divider" as const, journey: j },
    ...j.lanes.map((lane) => ({ kind: "lane" as const, journey: j, lane })),
  ]),
  { kind: "roles" },
  { kind: "closing" },
];
const TOTAL = SLIDES.length;

/* ---------- slide chrome ---------- */

const BRAND = <span className="flp-mark">CD</span>;

function Chrome({ section, color, n, light }: { section: string; color: string; n: number; light?: boolean }) {
  return (
    <div className={"flp-chrome" + (light ? " light" : "")}>
      {BRAND}
      <span className="flp-brandname">Condominio Costa Dorada&nbsp;I</span>
      <span className="flp-chrome-r">
        <span className="flp-section" style={{ color }}>{section}</span>
        <span className="flp-count">{String(n).padStart(2, "0")} / {TOTAL}</span>
      </span>
    </div>
  );
}

function SlideView({ slide, n, t }: { slide: Slide; n: number; t: (p: Pair) => string }) {
  if (slide.kind === "cover") {
    return (
      <div className="flp-frame navy">
        <div className="flp-coverin">
          <div className="flp-brandline">{BRAND}<span className="flp-brandname big">Condominio Costa Dorada&nbsp;I</span></div>
          <span className="flp-eyebrow gold">{t(P("Portal de Gestión · Referencia de flujos", "Management Portal · Flow reference"))}</span>
          <h1>{t(P("Flujos del sistema, por rol.", "The system's flows, by role."))}</h1>
          <p className="flp-lede">{t(P(
            "Cómo avanza el trabajo real de principio a fin en cada rol. Cada paso enlaza a la pantalla real del portal.",
            "How real work moves from start to finish in each role. Every step links to the real screen in the portal."
          ))}</p>
          <div className="flp-legend">
            {JOURNEYS.slice(1).map((j) => (
              <span className="flp-role" key={j.id}><i style={{ background: j.tint }} />{t(j.eyebrow)}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (slide.kind === "divider") {
    const j = slide.journey;
    return (
      <div className="flp-frame navy">
        <Chrome section={t(j.eyebrow)} color={j.tint} n={n} light />
        <div className="flp-divbody">
          <div className="flp-divtext">
            <span className="flp-eyebrow" style={{ color: j.tint }}>{t(j.eyebrow)}</span>
            <h2>{t(j.title)}</h2>
            <p>{t(j.blurb)}</p>
            <div className="flp-legend">
              {j.lanes.map((l, i) => (
                <span className="flp-scene" key={i}><i style={{ background: j.tint }} />{t(l.label)}</span>
              ))}
            </div>
            {j.footnote && <p className="flp-foot">{t(j.footnote)}</p>}
          </div>
          <div className="flp-divnum">{j.num}</div>
        </div>
      </div>
    );
  }

  if (slide.kind === "lane") {
    const { journey: j, lane } = slide;
    return (
      <div className="flp-frame sand">
        <Chrome section={t(j.eyebrow)} color={j.color} n={n} />
        <div className="flp-lanebody">
          <div className="flp-lanehead">
            <h2 style={{ color: j.color }}>{t(lane.label)}</h2>
            {j.id !== "inicio" && <Crumb iconName={j.entryIcon} title={t(P("Desde ", "From ")) + t(j.entryTitle)} route={j.entryRoute} color={j.color} />}
          </div>
          <Chain steps={lane.chain} t={t} color={j.color} />
        </div>
        <div className="flp-progress"><span style={{ width: `${(n / TOTAL) * 100}%`, background: j.tint }} /></div>
      </div>
    );
  }

  if (slide.kind === "roles") {
    return (
      <div className="flp-frame sand">
        <Chrome section={t(P("Roles y cargos", "Roles and seats"))} color={NAVY} n={n} />
        <div className="flp-rolesbody">
          <div className="flp-lanehead">
            <h2 style={{ color: NAVY }}>{t(P("Quién puede hacer qué", "Who can do what"))}</h2>
          </div>
          <table className="flp-roles">
            <thead>
              <tr>
                <th>{t(P("Rol / cargo", "Role / seat"))}</th>
                <th>{t(P("Cómo se asigna", "How it is assigned"))}</th>
                <th>{t(P("Puede", "Can"))}</th>
                <th>{t(P("No puede", "Cannot"))}</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((r) => (
                <tr key={r.role[0]}>
                  <td><strong>{t(r.role)}</strong></td>
                  <td className="who">{t(r.who)}</td>
                  <td><ul>{r.can.map((c, i) => <li key={i}>{t(c)}</li>)}</ul></td>
                  <td className="not">{r.not ? t(r.not) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="flp-roles-foot">{t(P(
            "Los cargos, los permisos de captura y «Gestionar finanzas» se asignan en Administración → Usuarios → Editar. Presidente, Vicepresidente, Secretaria y Tesorero tienen un solo titular a la vez.",
            "Seats, data-entry permissions and “Manage finances” are assigned under Administration → Users → Edit. President, Vice president, Secretary and Treasurer have one holder at a time."
          ))}</p>
        </div>
        <div className="flp-progress"><span style={{ width: `${(n / TOTAL) * 100}%`, background: NAVY_TINT }} /></div>
      </div>
    );
  }

  // closing
  return (
    <div className="flp-frame navy">
      <div className="flp-closein">
        <span className="flp-eyebrow gold">{t(P("Portal de Gestión", "Management Portal"))}</span>
        <h2>{t(P("19 pasos, 4 roles, un solo portal.", "19 steps, 4 roles, one portal."))}</h2>
        <p>{t(P(
          "Cada flujo de este resumen enlaza directo a su pantalla real. Vuelva a esta presentación cuando necesite recordar cómo se conecta el trabajo, de rol a rol.",
          "Every flow in this summary links directly to its real screen. Come back whenever you need a reminder of how the work connects, role to role."
        ))}</p>
        <Link to="/login" className="flp-ctabtn">{t(P("Entrar al portal", "Enter the portal"))}</Link>
      </div>
    </div>
  );
}

/* ---------- page ---------- */

export function FlujosPage() {
  const { t, i18n, lang } = useTr();
  const isEn = lang === "en";
  const [i, setI] = useState(() => {
    const n = parseInt(window.location.hash.replace("#", ""), 10);
    return Number.isFinite(n) ? Math.max(0, Math.min(TOTAL - 1, n - 1)) : 0;
  });
  const [scale, setScale] = useState(1);
  const [narrow, setNarrow] = useState(false);
  const [showIndex, setShowIndex] = useState(false);
  const [full, setFull] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const go = useCallback((n: number) => setI((p) => Math.max(0, Math.min(TOTAL - 1, typeof n === "number" ? n : p))), []);
  const next = useCallback(() => setI((p) => Math.min(TOTAL - 1, p + 1)), []);
  const prev = useCallback(() => setI((p) => Math.max(0, p - 1)), []);

  useEffect(() => {
    document.title = t(P("Flujos del Portal — Costa Dorada I", "Portal Flows — Costa Dorada I"));
  }, [isEn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fit = () => {
      const w = window.innerWidth, h = window.innerHeight;
      const isNarrow = w < 900;
      setNarrow(isNarrow);
      setScale(isNarrow ? 1 : Math.min(w / 1440, h / 810));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  // the slide number lives in the URL, so a slide can be linked and a reload keeps your place
  useEffect(() => {
    try { window.history.replaceState(null, "", `#${i + 1}`); } catch { /* history unavailable */ }
  }, [i]);

  // a pasted or edited #n in the address bar moves the deck
  useEffect(() => {
    const onHash = () => {
      const n = parseInt(window.location.hash.replace("#", ""), 10);
      if (Number.isFinite(n)) go(n - 1);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [go]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key;
      if (k === "Escape") { setShowIndex(false); return; }
      if (k === "ArrowRight" || k === "PageDown" || k === " ") { e.preventDefault(); next(); }
      else if (k === "ArrowLeft" || k === "PageUp") { e.preventDefault(); prev(); }
      else if (k === "Home") { e.preventDefault(); go(0); }
      else if (k === "End") { e.preventDefault(); go(TOTAL - 1); }
      else if (k === "g" || k === "G") setShowIndex((s) => !s);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, go]);

  useEffect(() => {
    const onFs = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFull = () => {
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      else rootRef.current?.requestFullscreen?.();
    } catch { /* fullscreen unavailable */ }
  };

  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { touch.current = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 46 && Math.abs(dx) > Math.abs(dy)) (dx < 0 ? next : prev)();
    touch.current = null;
  };

  const titles = useMemo(
    () => SLIDES.map((s) => {
      if (s.kind === "cover") return t(P("Portada", "Cover"));
      if (s.kind === "closing") return t(P("Cierre", "Closing"));
      if (s.kind === "divider") return t(s.journey.title);
      if (s.kind === "roles") return t(P("Roles y cargos", "Roles and seats"));
      return `${t(s.journey.title)} · ${t(s.lane.label)}`;
    }),
    [isEn] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const tone = (idx: number) => {
    const s = SLIDES[idx];
    if (s.kind === "divider" || s.kind === "lane") return s.journey.tint;
    return GOLD6;
  };

  return (
    <div className={"flp-root" + (narrow ? " narrow" : "")} ref={rootRef}>
      <style>{CSS}</style>

      <div className="flp-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="flp-deck" style={narrow ? undefined : { transform: `translate(-50%, -50%) scale(${scale})` }}>
          {SLIDES.map((s, idx) => (
            <div className={"flp-slide" + (idx === i ? " on" : "")} key={idx} aria-hidden={idx !== i}>
              <SlideView slide={s} n={idx + 1} t={t} />
            </div>
          ))}
        </div>
      </div>

      <div className="flp-rail"><span style={{ width: `${((i + 1) / TOTAL) * 100}%` }} /></div>

      <div className="flp-top">
        <Link to="/help" className="flp-pillbtn"><ArrowLeft size={15} />{t(P("Ayuda", "Help"))}</Link>
        <button type="button" className="flp-pillbtn" onClick={() => i18n.changeLanguage(isEn ? "es" : "en")}>
          <Globe size={15} />{isEn ? "Español" : "English"}
        </button>
      </div>

      <nav className="flp-ctl" aria-label={t(P("Controles de la presentación", "Presentation controls"))}>
        <button type="button" onClick={() => setShowIndex(true)} aria-label={t(P("Ver todas las láminas", "See all slides"))}>
          <LayoutGrid size={19} />
        </button>
        <span className="flp-sep" />
        <button type="button" onClick={prev} disabled={i === 0} aria-label={t(P("Lámina anterior", "Previous slide"))}>
          <ChevronLeft size={20} />
        </button>
        <span className="flp-ctlcount" aria-live="polite">{String(i + 1).padStart(2, "0")} / {TOTAL}</span>
        <button type="button" onClick={next} disabled={i === TOTAL - 1} aria-label={t(P("Lámina siguiente", "Next slide"))}>
          <ChevronRight size={20} />
        </button>
        <span className="flp-sep" />
        <button type="button" onClick={toggleFull} aria-label={t(P("Pantalla completa", "Fullscreen"))}>
          {full ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </nav>

      {showIndex && (
        <div className="flp-index" role="dialog" aria-modal="true">
          <div className="flp-indexhead">
            <h2>{t(P("Todas las láminas", "All slides"))}</h2>
            <button type="button" onClick={() => setShowIndex(false)} aria-label={t(P("Cerrar", "Close"))}><X size={20} /></button>
          </div>
          <div className="flp-indexgrid">
            {titles.map((ti, idx) => (
              <button type="button" key={idx} className={"flp-ix" + (idx === i ? " here" : "")} onClick={() => { go(idx); setShowIndex(false); }}>
                <i style={{ background: tone(idx) }} />
                <span className="flp-ixn">{String(idx + 1).padStart(2, "0")}</span>
                <span className="flp-ixt">{ti}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- scoped styles ---------- */

const CSS = `
.flp-root {
  --flp-navy8:#1c3f55; --flp-navy7:#1e4d6b; --flp-navy6:#2e6489;
  --flp-gold:#c8a560; --flp-gold-ink:#7a5a1e; --flp-gold-soft:#f4ecd6;
  --flp-sand:#faf6ee; --flp-surface:#ffffff; --flp-surface-2:#f3ead4; --flp-bandink:#f4ecda;
  --flp-ink:#21303a; --flp-muted:#5b6b73; --flp-faint:#8b96a0; --flp-line:#e7ddc9; --flp-line-2:#ded2b3;
  --flp-good:#2e7d5b; --flp-good-soft:#e3f0e9; --flp-coral:#b24e34; --flp-coral-soft:#f8e8e2;
  --serif:'Playfair Display', Georgia, serif;
  --sans:Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  --mono:ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  position:fixed; inset:0; overflow:hidden; background:var(--flp-navy950,#0c1e2c); color:var(--flp-bandink); font-family:var(--sans);
}
.flp-root *, .flp-root *::before, .flp-root *::after { box-sizing:border-box; }
.flp-root h1,.flp-root h2,.flp-root h3,.flp-root h4 { font-family:var(--serif); font-weight:700; margin:0; text-wrap:balance; }
.flp-root p { margin:0; text-wrap:pretty; }

.flp-stage { position:absolute; inset:0; display:block; background:radial-gradient(ellipse at 50% 42%, #143349 0%, #0c1e2c 72%); touch-action:pan-y; }
.flp-deck { position:fixed; top:50%; left:50%; width:1440px; height:810px; transform-origin:center center; box-shadow:0 34px 90px rgba(0,0,0,.55); }
.flp-slide { position:absolute; inset:0; opacity:0; visibility:hidden; transition:opacity .24s ease; }
.flp-slide.on { opacity:1; visibility:visible; }

.flp-frame { width:100%; height:100%; display:flex; flex-direction:column; overflow:hidden; }
.flp-frame.sand { background:var(--flp-sand); color:var(--flp-ink); }
.flp-frame.navy { background:linear-gradient(180deg, var(--flp-navy8) 0%, var(--flp-navy7) 55%, var(--flp-navy6) 100%); border-bottom:6px solid var(--flp-gold); }

.flp-mark { width:30px; height:30px; border-radius:8px; flex:none; display:grid; place-items:center; background:linear-gradient(150deg, var(--flp-gold), var(--flp-gold-ink)); color:#17324a; font-family:var(--serif); font-weight:700; font-size:14px; }
.flp-brandname { font-size:13.5px; letter-spacing:.2em; text-transform:uppercase; font-weight:700; color:var(--flp-muted); }
.flp-brandname.big { color:rgba(244,236,218,.82); font-size:15px; letter-spacing:.24em; }
.flp-eyebrow { font-size:15px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; }
.flp-eyebrow.gold { color:var(--flp-gold); }

.flp-chrome { display:flex; align-items:center; gap:15px; padding:40px 74px 0; flex:none; }
.flp-chrome.light .flp-brandname { color:rgba(244,236,218,.7); }
.flp-chrome-r { margin-left:auto; display:flex; align-items:center; gap:16px; }
.flp-section { font-size:13.5px; letter-spacing:.14em; text-transform:uppercase; font-weight:700; white-space:nowrap; }
.flp-count { font-family:var(--mono); font-size:14px; color:#93a0a3; font-variant-numeric:tabular-nums; white-space:nowrap; }
.flp-chrome.light .flp-count { color:rgba(244,236,218,.55); }
.flp-progress { display:flex; height:5px; flex:none; margin-top:auto; background:var(--flp-line); }
.flp-progress span { display:block; height:100%; }

/* cover */
.flp-coverin { flex-grow:1; display:flex; flex-direction:column; justify-content:center; gap:26px; padding:0 90px; }
.flp-brandline { display:flex; align-items:center; gap:14px; }
.flp-frame.navy .flp-coverin h1 { font-size:66px; line-height:1.05; color:var(--flp-bandink); max-width:16ch; }
.flp-lede { font-size:20px; line-height:1.55; color:rgba(244,236,218,.86); max-width:62ch; }
.flp-legend { display:flex; flex-wrap:wrap; gap:11px; }
.flp-role { display:inline-flex; align-items:center; gap:10px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.18); padding:9px 17px; border-radius:999px; font-size:16px; font-weight:600; color:var(--flp-bandink); }
.flp-role i, .flp-scene i { width:9px; height:9px; border-radius:50%; flex:none; }
.flp-scene { display:inline-flex; align-items:center; gap:9px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.16); padding:8px 16px; border-radius:999px; font-size:15px; font-weight:600; color:var(--flp-bandink); }

/* divider */
.flp-divbody { flex-grow:1; display:flex; align-items:center; gap:70px; padding:0 76px; }
.flp-divtext { flex-grow:1; display:flex; flex-direction:column; gap:22px; align-items:flex-start; }
.flp-divtext h2 { font-size:52px; line-height:1.08; color:var(--flp-bandink); max-width:16ch; }
.flp-divtext p { font-size:19px; line-height:1.55; color:rgba(244,236,218,.82); max-width:52ch; }
.flp-divnum { flex:none; font-family:var(--serif); font-weight:700; font-size:220px; line-height:.8; color:rgba(244,236,218,.1); }
.flp-foot { font-size:15px; color:rgba(244,236,218,.68); }

/* lane */
.flp-lanebody { flex-grow:1; display:flex; flex-direction:column; justify-content:center; align-items:center; gap:34px; padding:0 76px; }
.flp-lanehead { display:flex; align-items:center; gap:18px; }
.flp-lanehead h2 { font-size:42px; }
.flp-rolesbody { flex-grow:1; display:flex; flex-direction:column; gap:16px; padding:16px 64px 44px; min-height:0; }
.flp-rolesbody .flp-lanehead h2 { font-size:34px; }
.flp-roles { width:100%; border-collapse:separate; border-spacing:0; font-size:14px; line-height:1.35; background:var(--flp-surface); border:1px solid var(--flp-line); border-radius:12px; overflow:hidden; }
.flp-roles th { text-align:left; font-size:11px; letter-spacing:.08em; text-transform:uppercase; color:var(--flp-muted); padding:9px 14px; background:var(--flp-surface-2); }
.flp-roles td { padding:9px 14px; border-top:1px solid var(--flp-line); vertical-align:top; color:var(--flp-ink); }
.flp-roles td ul { margin:0; padding-left:16px; }
.flp-roles td.who { color:var(--flp-muted); font-size:13px; white-space:nowrap; }
.flp-roles td.not { color:var(--flp-coral); font-size:13px; }
.flp-roles-foot { font-size:13px; color:var(--flp-muted); }
.flp-root.narrow .flp-rolesbody { padding:16px 16px 90px; overflow:auto; }
.flp-root.narrow .flp-roles td.who { white-space:normal; }
.flp-crumb { display:inline-flex; align-items:center; gap:9px; font-size:16px; color:var(--flp-muted); text-decoration:none; padding:9px 16px; border:1px solid var(--flp-line); border-radius:999px; background:var(--flp-surface); }

/* chain */
.flp-chain { display:flex; align-items:center; justify-content:center; gap:14px; flex-wrap:wrap; }
.flp-chain-arrow { flex:none; width:118px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:5px; color:var(--flp-faint); }
.flp-chain-arrow span { font-size:14px; color:var(--flp-muted); text-align:center; line-height:1.3; max-width:100%; }

/* node */
.flp-node { display:flex; flex-direction:column; gap:9px; padding:24px 26px; background:var(--flp-surface); border:1px solid var(--flp-line); border-radius:16px; width:330px; flex:none; box-shadow:0 10px 26px -14px rgba(20,40,55,.25); text-decoration:none; color:inherit; }
.flp-node.info { border-style:dashed; background:transparent; }
.flp-node-top { display:flex; align-items:center; gap:10px; }
.flp-node-title { font-weight:700; font-size:23px; color:var(--flp-ink); }
.flp-node-desc { font-size:17px; color:var(--flp-muted); line-height:1.45; }
.flp-node-route { font-family:var(--mono); font-size:14px; color:var(--flp-faint); }
.flp-node-sub { font-size:15.5px; color:var(--flp-muted); }
.flp-node-sub b { color:var(--flp-ink); font-weight:700; }

/* outcome */
.flp-outcome { display:flex; align-items:center; gap:10px; font-size:19px; font-weight:700; padding:15px 24px; border-radius:999px; background:var(--flp-good-soft); color:var(--flp-good); border:1px solid color-mix(in oklab, var(--flp-good) 35%, transparent); white-space:nowrap; flex:none; }
.flp-outcome.warn { background:var(--flp-coral-soft); color:var(--flp-coral); border-color:color-mix(in oklab, var(--flp-coral) 35%, transparent); }

/* fork */
.flp-fork { display:flex; flex-direction:column; gap:14px; padding:22px 24px; border:1px dashed var(--flp-line-2); border-radius:16px; background:var(--flp-surface-2); width:360px; flex:none; }
.flp-fork-q { display:flex; align-items:center; gap:10px; font-size:19px; font-weight:700; color:var(--flp-ink); }
.flp-branches { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:10px; }
.flp-branches .flp-outcome { padding:12px 14px; font-size:16px; justify-content:center; white-space:normal; }
.flp-fork-svg { display:block; width:100%; height:auto; color:var(--flp-line-2); }

/* closing */
.flp-closein { flex-grow:1; display:flex; flex-direction:column; justify-content:center; gap:24px; padding:0 90px; align-items:flex-start; }
.flp-closein h2 { font-size:52px; line-height:1.1; color:var(--flp-bandink); max-width:18ch; }
.flp-closein p { font-size:19px; line-height:1.55; color:rgba(244,236,218,.84); max-width:58ch; }
.flp-ctabtn { display:inline-flex; align-items:center; gap:10px; background:var(--flp-gold); color:#17324a; padding:14px 24px; border-radius:12px; font-size:18px; font-weight:700; margin-top:6px; text-decoration:none; }
.flp-ctabtn:hover { background:var(--flp-gold-soft); }

/* deck chrome */
.flp-rail { position:absolute; left:0; right:0; bottom:0; height:3px; background:rgba(244,236,218,.12); z-index:20; }
.flp-rail span { display:block; height:100%; background:var(--flp-gold); transition:width .24s ease; }
.flp-top { position:absolute; top:20px; left:20px; right:20px; display:flex; justify-content:space-between; gap:12px; z-index:30; }
.flp-pillbtn { display:inline-flex; align-items:center; gap:8px; padding:9px 16px; border-radius:999px; background:rgba(9,24,35,.72); border:1px solid rgba(244,236,218,.18); color:var(--flp-bandink); font:inherit; font-size:14px; font-weight:600; cursor:pointer; text-decoration:none; backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); }
.flp-pillbtn:hover { background:rgba(244,236,218,.16); }
.flp-ctl { position:absolute; bottom:26px; left:50%; transform:translateX(-50%); display:flex; align-items:center; gap:6px; z-index:30; padding:7px 9px; border-radius:999px; background:rgba(9,24,35,.72); border:1px solid rgba(244,236,218,.18); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); box-shadow:0 10px 30px rgba(0,0,0,.4); }
.flp-ctl button { display:grid; place-items:center; width:38px; height:38px; padding:0; border:0; border-radius:50%; background:transparent; color:var(--flp-bandink); cursor:pointer; }
.flp-ctl button:hover:not(:disabled) { background:rgba(244,236,218,.13); }
.flp-ctl button:disabled { opacity:.32; cursor:default; }
.flp-ctlcount { font-family:var(--mono); font-size:14px; font-variant-numeric:tabular-nums; letter-spacing:.06em; color:rgba(244,236,218,.82); padding:0 10px; min-width:74px; text-align:center; }
.flp-sep { width:1px; height:22px; background:rgba(244,236,218,.18); flex:none; }
.flp-root button:focus-visible, .flp-root a:focus-visible { outline:2px solid var(--flp-gold); outline-offset:2px; }

.flp-index { position:absolute; inset:0; z-index:40; display:flex; flex-direction:column; gap:22px; padding:32px clamp(20px,5vw,72px); overflow-y:auto; background:rgba(8,21,31,.96); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px); }
.flp-indexhead { display:flex; align-items:center; justify-content:space-between; gap:16px; }
.flp-indexhead h2 { font-size:28px; color:var(--flp-bandink); }
.flp-indexhead button { background:transparent; border:0; color:var(--flp-bandink); cursor:pointer; padding:8px; border-radius:50%; display:grid; place-items:center; }
.flp-indexhead button:hover { background:rgba(244,236,218,.13); }
.flp-indexgrid { display:grid; grid-template-columns:repeat(auto-fill, minmax(258px, 1fr)); gap:10px; }
.flp-ix { display:flex; align-items:center; gap:13px; text-align:left; cursor:pointer; padding:13px 16px; border-radius:11px; color:var(--flp-bandink); background:rgba(244,236,218,.05); border:1px solid rgba(244,236,218,.16); font:inherit; font-size:15px; }
.flp-ix:hover { background:rgba(244,236,218,.11); border-color:rgba(244,236,218,.3); }
.flp-ix.here { border-color:var(--flp-gold); background:rgba(200,165,96,.14); }
.flp-ixn { font-family:var(--mono); font-size:13px; color:rgba(244,236,218,.5); font-variant-numeric:tabular-nums; }
.flp-ixt { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* narrow screens: reflow instead of shrink */
.flp-root.narrow .flp-deck { position:static; top:auto; left:auto; width:100%; height:100%; transform:none; box-shadow:none; }
.flp-root.narrow .flp-slide { overflow-y:auto; -webkit-overflow-scrolling:touch; }
.flp-root.narrow .flp-chrome { padding:128px 20px 0; gap:10px; }
.flp-root.narrow .flp-brandname { font-size:11px; letter-spacing:.16em; }
.flp-root.narrow .flp-section { font-size:11px; }
.flp-root.narrow .flp-count { font-size:12px; }
.flp-root.narrow .flp-eyebrow { font-size:12px; }

.flp-root.narrow .flp-coverin { padding:22px 20px; gap:18px; }
.flp-root.narrow .flp-frame.cover h1, .flp-root.narrow .flp-coverin h1 { font-size:clamp(32px,9vw,48px); }
.flp-root.narrow .flp-lede { font-size:16px; }
.flp-root.narrow .flp-role, .flp-root.narrow .flp-scene { font-size:14px; padding:8px 14px; }

.flp-root.narrow .flp-divbody { flex-direction:column; align-items:flex-start; justify-content:center; gap:18px; padding:22px 20px 90px; }
.flp-root.narrow .flp-divtext h2 { font-size:clamp(30px,8vw,44px); }
.flp-root.narrow .flp-divtext p { font-size:16px; }
.flp-root.narrow .flp-divnum { display:none; }

.flp-root.narrow .flp-lanebody { flex-direction:column; padding:22px 20px 90px; gap:18px; }
.flp-root.narrow .flp-lanehead { flex-direction:column; align-items:flex-start; gap:10px; }
.flp-root.narrow .flp-lanehead h2 { font-size:clamp(24px,6.4vw,32px); }
.flp-root.narrow .flp-chain { flex-direction:column; }
.flp-root.narrow .flp-chain-arrow { width:auto; flex-direction:row; padding:6px 0; }
.flp-root.narrow .flp-chain-arrow svg { transform:rotate(90deg); }
.flp-root.narrow .flp-node, .flp-root.narrow .flp-fork { width:100%; max-width:420px; }
.flp-root.narrow .flp-progress { display:none; }

.flp-root.narrow .flp-closein { padding:22px 20px 40px; }
.flp-root.narrow .flp-closein h2 { font-size:clamp(30px,8vw,44px); }
.flp-root.narrow .flp-closein p { font-size:16px; }

/* Moved to a second row under the back/language pills, not the bottom: in
   narrow mode a slide scrolls, and a bottom-fixed bar would permanently sit
   on top of whatever content happened to be scrolled underneath it. */
.flp-root.narrow .flp-ctl { top:64px; bottom:auto; }
.flp-root.narrow .flp-ctlcount { min-width:62px; font-size:13px; padding:0 6px; }

@media (prefers-reduced-motion: reduce) {
  .flp-slide, .flp-rail span, .flp-ix, .flp-pillbtn { transition:none; }
}
`;
